import React, { useEffect, useState, useCallback } from "react";
import CommonTable, { type TableColumn } from "@/components/common/CommonTable";
import MainHeading from "@/components/common/MainHeading";
import Layout from "@/components/layout/Layout";
import { Plus, Trash, TriangleAlert, Pencil } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { RoutesLink } from "@/utility/RouteLinks";
import { Button } from "@/components/ui/button";
import { CompanyDocumentServices } from "@/services/CompanyDocument/CompanyDocumentServices";
import TablePagination from "@/components/common/TablePagination";
import type { IPageInfo, IFilter } from "@/interface/common.interface";
import CommonModal from "@/components/common/CommonModal";
import { GetFilterUrl, showToastNotification } from "@/utility/helper";
import { ThrowError } from "@/api/callApi";
import CommonFilter from "@/components/common/CommonFilter";
import TableActions from "@/components/common/TableActions";
import moment from "moment";
import type { ICompanyDocument } from "@/interface/companyDocument.interface";

const TYPE_OPTIONS = [
  { label: "All", value: "ALL" },
  { label: "Policy", value: "POLICY" },
  { label: "Terms & Condition", value: "TERMS_CONDITION" },
  { label: "Letter", value: "LETTER" },
];

const CompanyDocument: React.FC = () => {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState<ICompanyDocument[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [paginationData, setPaginationData] = useState<IPageInfo>({
    currentPage: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  });

  const [filter, setFilter] = useState<IFilter>({
    search: "",
    status: "ALL",
  });

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string>("");

  const handleSearchChange = useCallback((value: string) => {
    setFilter((prev) => ({ ...prev, search: value }));
  }, []);

  const handleFilterChange = useCallback((value: string, name: string) => {
    setFilter((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleChangePageSize = (value: number) => {
    setPaginationData((prev) => ({ ...prev, pageSize: value, currentPage: 1 }));
  };

  const toggleDeleteDialog = useCallback((id: string = "") => {
    setDeleteId(id);
    setDeleteDialogOpen((prev) => !prev);
  }, []);

  const fetchData = async (
    currentPage = paginationData.currentPage,
    currentLimit = paginationData.pageSize,
  ) => {
    setIsLoading(true);
    try {
      const apiFilter = {
        search: filter.search || undefined,
        type: filter.status === "ALL" ? undefined : filter.status,
      };

      //    const apiFilter = {
      //     ...filter,
      //   };

      const paramsURL = GetFilterUrl(apiFilter);
      const query = `pageNumber=${currentPage}&pageSize=${currentLimit}${paramsURL ? `&${paramsURL}` : ""
        }`;

      const response = await CompanyDocumentServices.getCompanyDocuments(query);
      if (response.status) {
        setDocuments(response.data?.documents || []);
        setPaginationData((prev) => ({
          ...prev,
          total: response.data?.pageInfo?.total || 0,
          totalPages: response.data?.pageInfo?.totalPages || 0,
        }));
      } else {
        setDocuments([]);
      }
    } catch (error) {
      ThrowError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      const response =
        await CompanyDocumentServices.deleteCompanyDocument(deleteId);
      if (response?.status) {
        showToastNotification(
          "success",
          response?.message || "Document deleted successfully",
        );
        toggleDeleteDialog("");
        fetchData();
      }
    } catch (error) {
      ThrowError(error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [paginationData.currentPage, paginationData.pageSize, filter]);

  const columns: TableColumn<ICompanyDocument>[] = [
    {
      key: "sNo",
      header: "S.No",
      render: (_item, index) => (
        <span className="text-muted-foreground font-medium text-sm">
          {String(
            (paginationData.currentPage - 1) * paginationData.pageSize +
            index +
            1,
          ).padStart(2, "0")}
        </span>
      ),
    },
    {
      key: "title",
      header: "Title",
      render: (record) => record.title || "-",
    },
    {
      key: "documentKey",
      header: "Document Key",
      render: (record) => record.documentKey || "-",
    },
    {
      key: "type",
      header: "Type",
      render: (record) => record.type || "-",
    },
    {
      key: "version",
      header: "Version",
      render: (record) => record.version || "-",
    },
    {
      key: "effectiveDate",
      header: "Effective Date",
      render: (record) =>
        record.effectiveDate
          ? moment(record.effectiveDate).format("DD-MM-YYYY")
          : "-",
    },
    {
      key: "isActive",
      header: "Status",
      render: (record) => (
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${record.isActive
              ? "bg-green-100 text-green-800"
              : "bg-gray-100 text-gray-800"
            }`}
        >
          {record.isActive ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      render: (item) => (
        <TableActions
          onView={() =>
            navigate(`${RoutesLink.companyDocumentDetail}/${item.id}`)
          }
          viewLabel="View Details"
          actions={[
            {
              label: "Edit",
              onClick: () =>
                navigate(`${RoutesLink.editCompanyDocument}/${item.id}`),
              icon: <Pencil className="h-4 w-4" />,
              show: true,
            },
            {
              label: "Delete",
              onClick: () => toggleDeleteDialog(item.id),
              icon: <Trash className="h-4 w-4" />,
              className: "text-red-600",
              show: true,
            },
          ]}
        />
      ),
    },
  ];

  return (
    <Layout>
      <div className="space-y-6">
        <MainHeading
          title="Company Documents"
          buttonIcon={<Plus />}
          buttonText="Create Document"
          onClick={() => navigate(RoutesLink.createCompanyDocument)}
        />

        <CommonFilter
          searchValue={filter.search || ""}
          onSearchChange={handleSearchChange}
          value={filter}
          filterOptions={TYPE_OPTIONS}
          filterName="status"
          filterPlaceholder="Select Type"
          onChange={handleFilterChange}
        />

        <CommonTable columns={columns} data={documents} loading={isLoading} />

        <TablePagination
          paginationData={paginationData}
          setPaginationData={setPaginationData}
          handleChangePageSize={handleChangePageSize}
          isLoading={isLoading}
        />
      </div>

      <CommonModal
        show={deleteDialogOpen}
        onClose={() => toggleDeleteDialog("")}
        title=""
        className="max-w-md rounded-2xl p-0 overflow-hidden"
      >
        <div className="p-6">
          <div className="flex flex-col items-center text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
              <TriangleAlert className="text-red-600 w-8 h-8" />
            </div>
            <h2 className="mt-5 text-2xl font-bold text-gray-900">
              Delete Document?
            </h2>
            <p className="mt-3 text-sm leading-6 text-gray-500">
              This action cannot be undone. Once deleted, the document will be
              permanently removed.
            </p>
          </div>
          <div className="mt-8 flex gap-3">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => toggleDeleteDialog("")}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              className="flex-1"
              onClick={handleDelete}
            >
              Delete
            </Button>
          </div>
        </div>
      </CommonModal>
    </Layout>
  );
};

export default CompanyDocument;
